import { useState } from 'react';
import { nip19 } from 'nostr-tools';
import { AIMWindow } from './AIMWindow';
import { AIMButton } from './AIMButton';
import { AIMInput } from './AIMInput';
import { MessageSquare } from 'lucide-react';

interface NewIMDialogProps {
  onClose: () => void;
  onStartChat: (pubkey: string) => void;
}

export function NewIMDialog({ onClose, onStartChat }: NewIMDialogProps) {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);

    const trimmed = input.trim();
    if (!trimmed) {
      setError('Please enter an npub or hex pubkey');
      return;
    }

    let pubkey = trimmed;

    // Try to decode if it looks like an npub
    if (trimmed.startsWith('npub1')) {
      try {
        const decoded = nip19.decode(trimmed);
        if (decoded.type === 'npub') {
          pubkey = decoded.data;
        } else {
          setError('Invalid npub format');
          return;
        }
      } catch {
        setError('Invalid npub format');
        return;
      }
    } else if (trimmed.startsWith('nprofile1')) {
      try {
        const decoded = nip19.decode(trimmed);
        if (decoded.type === 'nprofile') {
          pubkey = decoded.data.pubkey;
        } else {
          setError('Invalid nprofile format');
          return;
        }
      } catch {
        setError('Invalid nprofile format');
        return;
      }
    } else {
      // Validate hex pubkey
      if (!/^[0-9a-fA-F]{64}$/.test(trimmed)) {
        setError('Invalid pubkey format. Use npub or 64-char hex');
        return;
      }
    }

    onStartChat(pubkey);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <AIMWindow
        title="Send Instant Message"
        icon={<MessageSquare className="w-4 h-4" />}
        onClose={onClose}
        className="w-[380px]"
        showMinimize={false}
      >
        <div className="p-4 space-y-4">
          <div className="text-sm">
            <p className="mb-2">Enter the screen name of the person you want to message:</p>
            <AIMInput
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="npub1... or hex pubkey"
              autoFocus
            />
          </div>

          {error && (
            <div className="text-red-600 text-xs bg-red-100 dark:bg-red-900/30 p-2 border border-red-300 dark:border-red-800">
              {error}
            </div>
          )}

          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
            <p>Tip: You can paste an npub (e.g., npub1abc...) or a hex public key.</p>
          </div>

          <div className="flex justify-end gap-2">
            <AIMButton onClick={onClose}>
              Cancel
            </AIMButton>
            <AIMButton variant="primary" onClick={handleSubmit}>
              OK
            </AIMButton>
          </div>
        </div>
      </AIMWindow>
    </div>
  );
}
